import type { DataTableMatrixBlock } from '@ui/chart/DataTableMatrix';
import type {
	PickupAlertBasis, PickupAlertSetting, PickupBooking, PickupBookings, PickupCell, PickupChange, PickupKind, PickupPeriod, PickupProperty, PickupRevenues,
	PropertyGroup, RevenueScope, VatBasis,
} from '@app/model/bi/Bi';

// Pick-up monitoring (ROO2-314 Part 2): the rules of the page on the answer of findPickupMonitoring. The figures come from the
// shared new-bookings rule (Part 1); this file only adds properties, months and kinds up, and divides for the ADR (D28).
// The Excel file of the server (PickupStayMonthBlocks) holds the same rules for the stay-month card; the fixture
// bi/pickup-stay-month.json of the app tests both, so that the file equals the screen. The same for the bookings card
// (PickupBookingRows, bi/pickup-bookings.json).

// D24: one Booked control for the whole page; default Today (D14).
export const PERIODS: { value: PickupPeriod, label: string }[] = [
	{ value: 'TODAY', label: 'Today' }, { value: 'YESTERDAY', label: 'Yesterday' }, { value: 'LAST3', label: 'Last 3 days' },
	{ value: 'LAST7', label: 'Last 7 days' }, { value: 'LAST14', label: 'Last 14 days' }, { value: 'CUSTOM', label: 'Custom' },
];
export const isPeriod = (value: string | null): value is PickupPeriod => PERIODS.some(p => p.value === value);
// D14: a custom period has 1 to 31 book days, the last one today at the latest (BiService.PICKUP_MAX_BOOK_DAYS).
export const MAX_BOOK_DAYS = 31;

const dayMs = (iso: string) => Date.parse(`${iso}T00:00:00Z`);
export const addDays = (iso: string, n: number): string => new Date(dayMs(iso) + n * 86400000).toISOString().slice(0, 10);
export const bookDays = (from: string, to: string): number => Math.round((dayMs(to) - dayMs(from)) / 86400000) + 1;
// The custom period that the server takes: both days, from ≤ to ≤ today and at most 31 book days.
export const customPeriodOk = (from: string | null, to: string | null, today: string): boolean =>
	from !== null && to !== null && /^\d{4}-\d{2}-\d{2}$/.test(from) && /^\d{4}-\d{2}-\d{2}$/.test(to) && from <= to && to <= today
	&& bookDays(from, to) <= MAX_BOOK_DAYS;

export const ddmm = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
// "Booked on 03/11/2026", "Booked 28/10/2026 – 03/11/2026".
export const bookedText = (from: string, to: string): string => from === to ? `Booked on ${ddmm(from)}` : `Booked ${ddmm(from)} – ${ddmm(to)}`;

// Q6: the change history of the bookings starts on 19/10/2026; a period from that day or before shows the note.
export const HISTORY_START = '2026-10-19';
export const showsHistoryNote = (bookFrom: string): boolean => bookFrom <= HISTORY_START;

export const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// The revenue of a cell, a booking or a reference in the switches of the page (the same choice as revenueOf of the other BI
// pages); null = no value.
export type Revenue = (cell: PickupRevenues) => number | null;
export const revenuePicker = (scope: RevenueScope, vat: VatBasis): Revenue => cell => scope === 'TOTAL'
	? (vat === 'NET' ? cell.totalRevenueNet : cell.totalRevenue)
	: (vat === 'NET' ? cell.roomRevenueNet : cell.roomRevenue);

// --- the sums of a kind (the KPI strip and the summary) ---------------------------------------------------------------------
// revenue null: a cell of the kind has no value of the revenue (ROO2-236 R2); the page shows "–".
export type Part = { bookings: number, roomNights: number, revenue: number | null };
export type Kinds = { fresh: Part, canc: Part, changes: Part, net: Part };
const emptyPart = (): Part => ({ bookings: 0, roomNights: 0, revenue: 0 });
const CHANGES: PickupChange[] = ['LONGER', 'SHORTER', 'PRICE'];

// The bookings of a kind come from the changes of the properties (a booking with nights in two months is one booking); the
// room nights and the revenue from the cells. Net bookings = every booking with a class (D25: made and cancelled in the
// period is in none).
export const kindsOf = (properties: PickupProperty[], revenue: Revenue): Kinds => {
	const k: Kinds = { fresh: emptyPart(), canc: emptyPart(), changes: emptyPart(), net: emptyPart() };
	const slot: Record<PickupKind, Part> = { NEW: k.fresh, CANCELLED: k.canc, CHANGES: k.changes, NET: k.net };
	properties.forEach(p => {
		p.cells.forEach(c => {
			const s = slot[c.kind];
			const value = revenue(c);
			s.roomNights += c.roomNights;
			s.revenue = s.revenue === null || value === null ? null : s.revenue + value;
		});
		p.changes.forEach(c => {
			if (c.change === 'NEW') k.fresh.bookings += c.bookings;
			else if (c.change === 'CANCELLED') k.canc.bookings += c.bookings;
			else if (CHANGES.includes(c.change)) k.changes.bookings += c.bookings;
			k.net.bookings += c.bookings;
		});
	});
	return k;
};
// ADR = revenue ÷ room nights (positive for the cancellations too); none without room nights or revenue.
export const adrOf = (p: Part): number | null => p.roomNights === 0 || p.revenue === null ? null : Math.abs(p.revenue) / Math.abs(p.roomNights);

// --- the stay-month card (D34–D43) ------------------------------------------------------------------------------------------
export type MatrixBasis = 'NEW' | 'NET';
// D35: the years with figures in the answer and the year of today, in order.
export const stayYearsOf = (properties: PickupProperty[], asOf: string): string[] => {
	const years = new Set(properties.flatMap(p => p.cells.map(c => c.stayMonth.slice(0, 4))));
	years.add(asOf.slice(0, 4));
	return [...years].sort();
};

export type MatrixScope = 'property' | 'group' | 'all';
export type MatrixGroup = { id: string, label: string, sublabel?: string, kind: 'total' | 'subtotal' | 'item', ids: number[] };
type Named = { companyHotelId: number, name: string };
const byName = (a: string, b: string) => a.localeCompare(b, 'en');
const word = (n: number) => `${n} ${n === 1 ? 'property' : 'properties'}`;

// The blocks of the table: one property = one block (D42); a Property group = "Group total", then each property (A–Z);
// All properties = "All properties", then each group marked "Subtotal on All properties" (A–Z) with its properties, then
// "In no manager group" with the others (D36). Without a marked group, All properties lists each property.
export const matrixGroups = (properties: Named[], scope: MatrixScope, groups: PropertyGroup[]): MatrixGroup[] => {
	const sorted = [...properties].sort((a, b) => byName(a.name, b.name));
	const item = (p: Named): MatrixGroup => ({ id: String(p.companyHotelId), label: p.name, kind: 'item', ids: [p.companyHotelId] });
	if (scope === 'property') return sorted.map(item);
	const top: MatrixGroup = { id: 'total', label: scope === 'all' ? 'All properties' : 'Group total', sublabel: word(sorted.length), kind: 'total',
		ids: sorted.map(p => p.companyHotelId) };
	if (scope === 'group') return [top, ...sorted.map(item)];
	const marked = groups.filter(g => g.subtotalOnAll).sort((a, b) => byName(a.title, b.title));
	const inGroup = new Set(marked.flatMap(g => g.companyHotelIds));
	const out: MatrixGroup[] = [top];
	marked.forEach(g => {
		const members = sorted.filter(p => g.companyHotelIds.includes(p.companyHotelId));
		if (members.length === 0) return;
		out.push({ id: `group:${g.id}`, label: g.title, sublabel: `Subtotal · ${word(members.length)}`, kind: 'subtotal', ids: members.map(p => p.companyHotelId) },
			...members.map(item));
	});
	const rest = sorted.filter(p => !inGroup.has(p.companyHotelId));
	if (marked.length > 0 && rest.length > 0) {
		out.push({ id: 'rest', label: 'In no manager group', sublabel: `Subtotal · ${word(rest.length)}`, kind: 'subtotal', ids: rest.map(p => p.companyHotelId) },
			...rest.map(item));
	} else if (marked.length === 0) out.push(...sorted.map(item));
	return out;
};

// The room nights and the revenue of a month or the year; the revenue stays null once a cell has none.
type Sum = { roomNights: number, revenue: number | null };
const add = (s: Sum, c: PickupCell, revenue: Revenue): Sum => {
	const value = revenue(c);
	return { roomNights: s.roomNights + c.roomNights, revenue: s.revenue === null || value === null ? null : s.revenue + value };
};
const rounded = (v: number | null) => v === null ? null : Math.round(v);
const adr = (s: Sum) => s.revenue === null || s.roomNights <= 0 ? null : Math.round(s.revenue / s.roomNights);

// One block per group: Revenue, Room nights and, on New bookings, ADR per month of the stay year and the year total (D34,
// D41: no ADR row on the net). Revenue and ADR are whole euros; the total ADR is the revenue of the year ÷ its room nights.
export const matrixBlocks = (properties: PickupProperty[], groups: MatrixGroup[], year: string, basis: MatrixBasis, revenue: Revenue): DataTableMatrixBlock[] => {
	const kind: PickupKind = basis === 'NEW' ? 'NEW' : 'NET';
	return groups.map(g => {
		const ids = new Set(g.ids);
		const months: Sum[] = MONTH_SHORT.map(() => ({ roomNights: 0, revenue: 0 }));
		let total: Sum = { roomNights: 0, revenue: 0 };
		properties.filter(p => ids.has(p.companyHotelId)).forEach(p => p.cells.forEach(c => {
			if (c.kind !== kind || !c.stayMonth.startsWith(`${year}-`)) return;
			const m = Number(c.stayMonth.slice(5, 7)) - 1;
			months[m] = add(months[m], c, revenue);
			total = add(total, c, revenue);
		}));
		const values: DataTableMatrixBlock['values'] = {
			revenue: { cells: months.map(s => rounded(s.revenue)), total: rounded(total.revenue) },
			roomNights: { cells: months.map(s => s.roomNights), total: total.roomNights },
		};
		if (basis === 'NEW') values.adr = { cells: months.map(adr), total: adr(total) };
		return { id: g.id, label: g.label, sublabel: g.sublabel, kind: g.kind, values };
	});
};

// "Pick-up monitoring <scope> <New bookings | Net pick-up> Jan-Dec <year> <as-of date>.xlsx", as the server names it.
export const pickupFileName = (name: string | null, basis: MatrixBasis, year: string, asOf: string): string =>
	`Pick-up monitoring ${(name ?? 'All properties').replace(/[\\/:*?"<>|]/g, ' ').replace(/\s+/g, ' ').trim()} ${basis === 'NEW' ? 'New bookings' : 'Net pick-up'} Jan-Dec ${year} ${asOf}.xlsx`;

// --- the Property groups drawer (item 7, D36) -------------------------------------------------------------------------------
// The properties of the edited group that another marked group holds too: the save refuses them, so the drawer names each.
export const subtotalOverlaps = (groups: PropertyGroup[], editingId: number | null, selected: number[], marked: boolean): { companyHotelId: number, group: string }[] =>
	!marked ? [] : groups.filter(g => g.id !== editingId && g.subtotalOnAll)
		.flatMap(g => selected.filter(id => g.companyHotelIds.includes(id)).map(id => ({ companyHotelId: id, group: g.title })));

// --- the ADR alerts and the bookings list (step 2b: B2, B3, D2–D13) ----------------------------------------------------------
export const DEFAULT_ALERT: PickupAlertSetting = { basis: 'ROOM_TYPE', direction: 'BOTH', threshold: 20 };
// D5: the bases of Compare with. "flagged" = the share of the 7,999 test bookings (28/09–27/10/2026, 34 properties, more than
// 20 %, below or above) that each basis flags.
export const BASES: { value: PickupAlertBasis, label: string, short: string, vs: string, flagged: string, info: string }[] = [
	{ value: 'ROOM_TYPE', label: 'Same room type, same nights', short: 'Room type ADR', vs: 'vs Room type', flagged: '23 %',
		info: 'the ADR on the books now of the booked room type, on each night of the booking (weighted by its room nights). The booking itself counts in it.' },
	{ value: 'HOTEL_NIGHTS', label: 'Hotel ADR of the stay nights', short: 'Hotel ADR', vs: 'vs Hotel', flagged: '38 %',
		info: 'the hotel ADR on the books now of each night of the booking (the ADR of Daily performance), weighted by its room nights.' },
	{ value: 'HOTEL_MONTH', label: 'Hotel ADR of the arrival month', short: 'Month ADR', vs: 'vs Month', flagged: '52 %',
		info: 'the hotel ADR on the books now of the arrival month of the booking.' },
	{ value: 'BOOKED_DAY', label: 'ADR of bookings received that day', short: 'Day bookings ADR', vs: 'vs Day bookings', flagged: '59 %',
		info: 'the ADR of all new bookings that the property received on the same day.' },
];
export const basisOf = (basis: PickupAlertBasis) => BASES.find(b => b.value === basis) ?? BASES[0];
export const CHANGE_LABEL: Record<PickupChange, string> = { NEW: 'New', LONGER: 'Longer', SHORTER: 'Shorter', PRICE: 'Price', CANCELLED: 'Cancelled' };

// The book day (YYYY-MM-DD) and the booking time (dd/mm/yy hh:mm) of a booking: the server sends the hotel time.
export const bookDayOf = (at: string | null): string | null => at === null ? null : at.slice(0, 10);
export const bookTimeOf = (at: string | null): string => at === null ? '–' : `${at.slice(8, 10)}/${at.slice(5, 7)}/${at.slice(2, 4)} ${at.slice(11, 16)}`;

export type BookingRow = PickupBooking & { companyHotelId: number, adr: number | null, reference: number | null, deviation: number | null };
// B3: the booking ADR on the books now (revenue now ÷ room nights now); the reference of the basis, none when it is 0 or
// below (a reference with no priced night); deviation = ADR ÷ reference − 1. The ADR of bookings received that day = the
// revenue now ÷ the room nights now of the NEW bookings of the property with the same book day, in this answer. Newest first
// (a booking with no booking time last), as PickupBookingRows of the Excel file.
export const bookingRows = (answer: PickupBookings, basis: PickupAlertBasis, revenue: Revenue): BookingRow[] => answer.properties.flatMap(p => {
	const day = new Map<string, { rn: number, rev: number }>();
	p.bookings.filter(b => b.change === 'NEW').forEach(b => {
		const key = bookDayOf(b.bookedAt) ?? '';
		const v = day.get(key) ?? { rn: 0, rev: 0 };
		v.rn += b.roomNightsNow;
		v.rev += (b.revenueNow === null ? null : revenue(b.revenueNow)) ?? 0;
		day.set(key, v);
	});
	return p.bookings.map(b => {
		const now = b.revenueNow === null ? null : revenue(b.revenueNow);
		const adr = b.roomNightsNow > 0 && now !== null ? now / b.roomNightsNow : null;
		const d = day.get(bookDayOf(b.bookedAt) ?? '');
		const of = (r: PickupRevenues | null) => r === null ? null : revenue(r);
		const raw = b.roomNightsNow === 0 ? null : basis === 'ROOM_TYPE' ? of(b.refRoomType) : basis === 'HOTEL_NIGHTS' ? of(b.refHotelNights)
			: basis === 'HOTEL_MONTH' ? of(b.refArrivalMonth) : (d === undefined || d.rn === 0 ? null : d.rev / d.rn);
		const reference = raw === null || raw <= 0 ? null : raw;
		const deviation = adr === null || reference === null ? null : adr / reference - 1;
		return { ...b, companyHotelId: p.companyHotelId, adr, reference, deviation };
	});
}).sort((a, b) => (b.bookedAt ?? '').localeCompare(a.bookedAt ?? ''));

// D8, D12: an alert = a booking on the books (new, longer, shorter, price) whose deviation passes the threshold in the direction.
export const isAlert = (b: BookingRow, setting: PickupAlertSetting): boolean => {
	if (b.change === 'CANCELLED' || b.deviation === null) return false;
	const t = setting.threshold / 100;
	if (setting.direction === 'BELOW') return b.deviation < -t;
	if (setting.direction === 'ABOVE') return b.deviation > t;
	return Math.abs(b.deviation) > t;
};
// The alerts of a setting, the largest deviation first.
export const alertsOf = (rows: BookingRow[], setting: PickupAlertSetting): BookingRow[] =>
	rows.filter(r => isAlert(r, setting)).sort((a, b) => Math.abs(b.deviation ?? 0) - Math.abs(a.deviation ?? 0));
// A threshold that the server takes: a whole number from 1 to 100.
export const thresholdOk = (value: number): boolean => Number.isInteger(value) && value >= 1 && value <= 100;

// "Pick-up monitoring <scope> Bookings <Current view | All data> <month(s) of the period> <as-of date>.xlsx", as the server names it.
const monthRange = (from: string, to: string): string => {
	const f = `${MONTH_SHORT[Number(from.slice(5, 7)) - 1]} ${from.slice(0, 4)}`, t = `${MONTH_SHORT[Number(to.slice(5, 7)) - 1]} ${to.slice(0, 4)}`;
	if (f === t) return f;
	return from.slice(0, 4) === to.slice(0, 4) ? `${MONTH_SHORT[Number(from.slice(5, 7)) - 1]}-${t}` : `${f}-${t}`;
};
export const pickupBookingsFileName = (name: string | null, file: 'CURRENT_VIEW' | 'ALL_DATA', bookFrom: string, bookTo: string, asOf: string): string =>
	`Pick-up monitoring ${(name ?? 'All properties').replace(/[\\/:*?"<>|]/g, ' ').replace(/\s+/g, ' ').trim()} Bookings ${file === 'ALL_DATA' ? 'All data' : 'Current view'} ${
		monthRange(bookFrom, bookTo)} ${asOf}.xlsx`;
